import { useState, useRef, useCallback } from "react";
import Cropper from "react-cropper";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { Crop, Eraser, RotateCw, RotateCcw, FlipHorizontal, FlipVertical, Download, Loader2, Upload, ZoomIn, ZoomOut } from "lucide-react";
import "react-cropper/node_modules/cropperjs/dist/cropper.css";

const ImageEditor = ({ isOpen, onClose, onSave, initialImage = null }) => {
  const [image, setImage] = useState(initialImage);
  const [editedImage, setEditedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("crop");
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
        setEditedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const getCropData = () => {
    if (cropperRef.current?.cropper) {
      const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas({
        maxWidth: 1024,
        maxHeight: 1024,
      });
      if (croppedCanvas) {
        const croppedImage = croppedCanvas.toDataURL("image/png");
        setEditedImage(croppedImage);
        toast.success("Image cropped successfully");
      }
    }
  };

  const rotate = (degrees) => {
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.rotate(degrees);
    }
  };

  const flip = (direction) => {
    if (cropperRef.current?.cropper) {
      const data = cropperRef.current.cropper.getData();
      if (direction === "horizontal") {
        cropperRef.current.cropper.scaleX(data.scaleX === 1 ? -1 : 1);
      } else {
        cropperRef.current.cropper.scaleY(data.scaleY === 1 ? -1 : 1);
      }
    }
  };

  const zoom = (ratio) => {
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.zoom(ratio);
    }
  };

  const removeBackground = useCallback(async () => {
    if (!image) return;

    // Background removal requires external service in production
    toast.info(
      "Background removal is not available in this version. Please use an external service like remove.bg or Canva for background removal.",
      { duration: 5000 }
    );
  }, [image]);

  const handleSave = () => {
    const finalImage = editedImage || image;
    if (finalImage) {
      onSave(finalImage);
      onClose();
    }
  };

  const handleDownload = () => {
    const finalImage = editedImage || image;
    if (finalImage) {
      const link = document.createElement("a");
      link.download = `edited-image-${Date.now()}.png`;
      link.href = finalImage;
      link.click();
    }
  };

  const reset = () => {
    setEditedImage(null);
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.reset();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Crop className="w-5 h-5" />
            Image Editor
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!image ? (
            <div 
              className="border-2 border-dashed border-slate-600 rounded-lg p-12 text-center cursor-pointer hover:border-[#d4af37] transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-400 mb-2">Click to upload an image</p>
              <p className="text-xs text-slate-500">PNG, JPG, JPEG, GIF, WEBP, BMP, SVG, TIFF</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/svg+xml,image/tiff,image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          ) : (
            <>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-slate-700 w-full">
                  <TabsTrigger value="crop" className="flex-1 data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f172a]">
                    <Crop className="w-4 h-4 mr-2" /> Crop & Transform
                  </TabsTrigger>
                  <TabsTrigger value="background" className="flex-1 data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f172a]">
                    <Eraser className="w-4 h-4 mr-2" /> Remove Background
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="crop" className="space-y-4">
                  {/* Toolbar */}
                  <div className="flex flex-wrap gap-2 p-2 bg-slate-700 rounded-lg">
                    <Button variant="ghost" size="sm" onClick={() => rotate(-90)} className="text-slate-300 hover:text-white">
                      <RotateCcw className="w-4 h-4 mr-1" /> -90°
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => rotate(90)} className="text-slate-300 hover:text-white">
                      <RotateCw className="w-4 h-4 mr-1" /> +90°
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => flip("horizontal")} className="text-slate-300 hover:text-white">
                      <FlipHorizontal className="w-4 h-4 mr-1" /> Flip H
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => flip("vertical")} className="text-slate-300 hover:text-white">
                      <FlipVertical className="w-4 h-4 mr-1" /> Flip V
                    </Button>
                    <div className="h-8 w-px bg-slate-600" />
                    <Button variant="ghost" size="sm" onClick={() => zoom(0.1)} className="text-slate-300 hover:text-white">
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => zoom(-0.1)} className="text-slate-300 hover:text-white">
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={reset} className="text-red-400 hover:text-red-300">
                      Reset
                    </Button>
                  </div>

                  {/* Cropper */}
                  <div className="bg-slate-900 rounded-lg overflow-hidden" style={{ height: "400px" }}>
                    <Cropper
                      ref={cropperRef}
                      src={image}
                      style={{ height: "100%", width: "100%" }}
                      aspectRatio={NaN}
                      guides={true}
                      viewMode={1}
                      background={false}
                      responsive={true}
                      autoCropArea={0.8}
                    />
                  </div>

                  <Button onClick={getCropData} className="w-full bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90">
                    <Crop className="w-4 h-4 mr-2" /> Apply Crop
                  </Button>
                </TabsContent>

                <TabsContent value="background" className="space-y-4">
                  <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-center" style={{ minHeight: "400px" }}>
                    <img 
                      src={editedImage || image} 
                      alt="Preview" 
                      className="max-w-full max-h-[380px] object-contain rounded"
                      style={{ backgroundImage: "linear-gradient(45deg, #374151 25%, transparent 25%), linear-gradient(-45deg, #374151 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #374151 75%), linear-gradient(-45deg, transparent 75%, #374151 75%)", backgroundSize: "20px 20px", backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px" }}
                    />
                  </div>

                  <Button 
                    onClick={removeBackground} 
                    disabled={isProcessing}
                    className="w-full bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                      </>
                    ) : (
                      <>
                        <Eraser className="w-4 h-4 mr-2" /> Remove Background
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-slate-400 text-center">
                    AI-powered background removal works best with clear subject boundaries
                  </p>
                </TabsContent>
              </Tabs>

              {/* Preview of edited image */}
              {editedImage && (
                <div className="p-4 bg-slate-700 rounded-lg">
                  <p className="text-sm text-slate-300 mb-2">Edited Preview:</p>
                  <img 
                    src={editedImage} 
                    alt="Edited" 
                    className="max-w-full max-h-32 object-contain rounded mx-auto"
                    style={{ backgroundImage: "linear-gradient(45deg, #1f2937 25%, transparent 25%), linear-gradient(-45deg, #1f2937 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1f2937 75%), linear-gradient(-45deg, transparent 75%, #1f2937 75%)", backgroundSize: "10px 10px", backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0px" }}
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-700">
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-slate-600"
                >
                  <Upload className="w-4 h-4 mr-2" /> Change Image
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex-1" />
                <Button variant="outline" onClick={handleDownload} className="border-slate-600">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
                <Button onClick={handleSave} className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90">
                  Save & Use
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditor;
